// @ts-nocheck

import { useState, useRef, useLayoutEffect } from 'react';
import type { ElementRef } from 'react';
import { useSpring } from 'react-spring/web.cjs';
import { useGesture } from 'react-use-gesture';

import { useViewportContext } from '../contexts/ViewportContext';
import { getDimensions } from '../lib/core/dom/dimensions';
import { ViewportOrientations } from '../types/viewport';

import usePictureInPicture from './usePictureInPicture';
import usePrevious from './usePrevious';

// PIP PORTAIT RATIOS
const DEFAULT_PORTRAIT_WIDTH_RATIO = 65 / 100; // the PiP takes 65% of the screen when it appears for the first time
const MIN_WIDTH_PORTRAIT_RATIO = 54 / 100; // 54 pourcent because it is the ratio that we want to get a min width of 200px

// PIP LANDSCAPE RATIOS
const DEFAULT_LANDSCAPE_WIDTH_RATIO = 40 / 100;
const MAX_WIDTH_LANDSCAPE_RATIO = 50 / 100;
const MIN_WIDTH_LANDSCAPE_RATIO = 30 / 100;

const PINCH_SENSITIVITY = 0.05;
const PINCH_MIN_DISTANCE_BOUNDS = 1; // min scaling value for the pinch
const PINCH_MAX_DISTANCE_BOUNDS = 30; // max scaling value for the pinch
const WTV_HEADER_HEIGHT = 11; // extra space to avoid the custom pip to be cropped with the tab bar due to the add of the little header

/**
 * ONLY FOR MOBILE
 * returns all the necessary gesture events and style
 *
 * @param {ElementRef<'div'>} domElem the target element where the gesture is applied
 * @param {boolean} isActive activate or not the gesture
 */
const useTouchDragAndResize = (domElem: ElementRef<'div'>, isActive: boolean = true) => {
  const {
    width: viewportWidth,
    height: viewportHeight,
    navbarHeight,
    orientation,
    tabbarHeight,
  } = useViewportContext();
  const [style, set] = useSpring(() => ({ x: 0, y: 0, width: 0 }));
  const initialSize = useRef({ width: 0, height: 0, ratio: 0, y: 0, x: 0 });
  const currentSize = useRef({ x: 0, y: 0, width: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const previousDragging = usePrevious(isDragging);
  const shouldLockPosition = !isDragging && previousDragging;
  const { getStreamWtvOpen, isFullscreen } = usePictureInPicture();
  const isLandscape = orientation === ViewportOrientations.LANDSCAPE;
  const isStreamWtvOpen = getStreamWtvOpen();

  const bind = useGesture(
    {
      onDrag: ({ delta, dragging }) => {
        if (!isActive || !domElem) {
          return;
        }

        const { width: videoWidth, height: videoHeight } = getDimensions(domElem);
        setIsDragging(dragging);
        set(() => {
          const limitW = viewportWidth - videoWidth;
          const limitH = viewportHeight - videoHeight - tabbarHeight;
          const x = Math.max(0, Math.min(currentSize.current.x + delta[0], limitW));
          const y = Math.max(navbarHeight, Math.min(currentSize.current.y + delta[1], limitH));
          currentSize.current = { width: videoWidth, x, y };
          return { x, y, width: videoWidth };
        });
      },
      onPinch: ({ offset: [scale], first }) => {
        // NB: ONLY for the stream video
        if (!isActive || !domElem || isStreamWtvOpen) {
          return;
        }
        setIsDragging(false);

        set(() => {
          const { width: videoWidth } = currentSize.current;
          const videoHeight = videoWidth * initialSize.current.ratio;
          const minRelWidth = viewportWidth * (isLandscape ? MIN_WIDTH_LANDSCAPE_RATIO : MIN_WIDTH_PORTRAIT_RATIO);
          const maxRelWidth = isLandscape ? viewportWidth * MAX_WIDTH_LANDSCAPE_RATIO : viewportWidth;
          const customScale = initialSize.current.width * scale * PINCH_SENSITIVITY;

          // when gesture consider its a first event we reset intial values
          if (first) {
            initialSize.current.width = videoWidth;
            initialSize.current.y = currentSize.current.y;
          }

          let newWidth = Math.max(Math.min(customScale, maxRelWidth), minRelWidth);

          const diffWidth = newWidth - videoWidth;
          let x = currentSize.current.x - diffWidth / 2;
          x = Math.max(0, Math.min(x, viewportWidth - newWidth));

          if (x + newWidth > viewportWidth) {
            newWidth = viewportWidth - x;
          }

          const newHeight = newWidth * initialSize.current.ratio;
          const diffHeight = newHeight - videoHeight;
          let y = initialSize.current.y - diffHeight / 2;
          y = Math.max(navbarHeight, Math.min(y, viewportHeight - newHeight - tabbarHeight));

          // lock the pip on the corners after using the pinch event
          const middleX = x + newWidth / 2;
          const middleY = y + newHeight / 2;
          x = middleX > viewportWidth / 2 ? viewportWidth - newWidth : 0;
          y = middleY > viewportHeight / 2 ? viewportHeight - newHeight - tabbarHeight : navbarHeight;

          currentSize.current = { width: newWidth, x, y };
          return { x, y, width: newWidth };
        });
      },
    },
    {
      drag: {
        useTouch: true,
      },
      pinch: {
        distanceBounds: {
          min: PINCH_MIN_DISTANCE_BOUNDS,
          max: PINCH_MAX_DISTANCE_BOUNDS,
        },
      },
    },
  );

  // set initial values
  useLayoutEffect(() => {
    if (domElem) {
      // When the WTV pip is activated, we need to add an extra height to place correctly the video above the navbar
      // The reason is that the WTV pip has a header
      const extraHeight = isStreamWtvOpen ? WTV_HEADER_HEIGHT : 0;
      const { width: firstWidth, height: firstHeight } = getDimensions(domElem);
      const ratio = (firstHeight + extraHeight) / firstWidth;
      initialSize.current = { width: firstWidth, height: firstHeight, ratio, y: 0, x: 0 };
    }
  }, [domElem, isActive, isStreamWtvOpen]);

  useLayoutEffect(() => {
    if (!isActive) {
      set({ x: 0, y: 0, width: viewportWidth });
    }
  }, [viewportWidth, isActive, set]);

  // place video when entering floating pip mode
  useLayoutEffect(() => {
    if (isActive && domElem) {
      const { ratio } = initialSize.current;
      const relWidth = viewportWidth * (isLandscape ? DEFAULT_LANDSCAPE_WIDTH_RATIO : DEFAULT_PORTRAIT_WIDTH_RATIO);
      const minRelWidth = viewportWidth * (isLandscape ? MIN_WIDTH_LANDSCAPE_RATIO : MIN_WIDTH_PORTRAIT_RATIO);
      let width = Math.max(Math.min(relWidth, viewportWidth), minRelWidth);
      let height = width * ratio;

      // handle max height (mostly for landscape)
      const limitHeight = viewportHeight - tabbarHeight - navbarHeight;
      if (height > limitHeight) {
        height = limitHeight;
        width = height / ratio;
      }

      const x = viewportWidth - width;
      const y = viewportHeight - height - tabbarHeight;
      initialSize.current = { width, height, ratio, y, x };
      currentSize.current = { x, y, width };
      set({ x, y, width });
    }
  }, [isActive, viewportWidth, viewportHeight, tabbarHeight, navbarHeight, set, domElem, isLandscape]);

  // lock to corner when finish dragging
  useLayoutEffect(() => {
    if (!shouldLockPosition || !domElem) {
      return;
    }
    const { ratio } = initialSize.current;
    const { x: left, y: top, width } = currentSize.current;
    const height = width * ratio;
    const middleX = left + width / 2;
    const middleY = top + height / 2;
    const x = middleX > viewportWidth / 2 ? viewportWidth - width : 0;
    const y = middleY > viewportHeight / 2 ? viewportHeight - height - tabbarHeight : navbarHeight;
    initialSize.current = { width, height, ratio, y, x };
    currentSize.current = { x, y, width };
    set({ x, y, width });
  }, [domElem, isFullscreen, navbarHeight, set, shouldLockPosition, tabbarHeight, viewportWidth, viewportHeight]);

  return { bind, style, isDragging };
};

export default useTouchDragAndResize;
